/**
 * OSD (Operator Systemu Dystrybucyjnego) Types
 *
 * @description Type definitions for multi-OSD pricing system
 * @version 1.0.0
 * @created 2025-01-05
 *
 * Supports 4 main Polish electricity distributors:
 * - TAURON Dystrybucja (southern Poland)
 * - PGE Dystrybucja (central/eastern Poland)
 * - ENEA Operator (western Poland)
 * - ENERGA-Operator (northern Poland)
 */

// ============================================================================
// OSD CODES
// ============================================================================

/**
 * OSD operator codes
 */
export type OSDCode = 'TAURON' | 'PGE' | 'ENEA' | 'ENERGA';

/**
 * All supported OSD codes
 */
export const OSD_CODES: readonly OSDCode[] = ['TAURON', 'PGE', 'ENEA', 'ENERGA'] as const;

// ============================================================================
// TARIFF CODES
// ============================================================================

/**
 * Universal tariff codes (available at all OSD)
 */
export type UniversalTariffCode = 'G11' | 'G12' | 'G12w';

/**
 * Conditional tariff codes (available at some OSD)
 */
export type ConditionalTariffCode = 'G12as';

/**
 * OSD-specific tariff codes
 */
export type OSDSpecificTariffCode = 'G12n' | 'G12r';

/**
 * Tauron-only advanced tariff codes
 */
export type AdvancedTariffCode = 'G13' | 'G13s' | 'G14';

/**
 * Business tariff codes (C-series)
 * C1 Group: ≤40 kW contracted power (small business)
 * C2 Group: >40 kW contracted power (medium/large business)
 */
export type BusinessTariffCode = 'C11' | 'C12a' | 'C12b' | 'C21' | 'C22a' | 'C22b';

/**
 * Household tariff codes (G-series)
 */
export type HouseholdTariffCode =
  | UniversalTariffCode
  | ConditionalTariffCode
  | OSDSpecificTariffCode
  | AdvancedTariffCode;

/**
 * All possible tariff codes (household + business)
 */
export type TariffCode = HouseholdTariffCode | BusinessTariffCode;

/**
 * Household tariff availability matrix per OSD (G-series)
 */
export const TARIFF_AVAILABILITY: Record<OSDCode, HouseholdTariffCode[]> = {
  TAURON: ['G11', 'G12', 'G12w', 'G12as', 'G13', 'G13s', 'G14'],
  PGE: ['G11', 'G12', 'G12w', 'G12n'],
  ENEA: ['G11', 'G12', 'G12w', 'G12as'],
  ENERGA: ['G11', 'G12', 'G12w', 'G12as', 'G12r'],
} as const;

/**
 * Business tariff availability matrix per OSD (C-series)
 * Based on URE 2026 tariff approvals
 */
export const BUSINESS_TARIFF_AVAILABILITY: Record<OSDCode, BusinessTariffCode[]> = {
  TAURON: ['C11', 'C12a', 'C12b', 'C21', 'C22a', 'C22b'],
  PGE: ['C11', 'C12a', 'C12b', 'C21', 'C22a', 'C22b'],
  ENEA: ['C11', 'C12a', 'C12b', 'C21', 'C22a', 'C22b'],
  ENERGA: ['C11', 'C12a', 'C12b', 'C21', 'C22a', 'C22b'],
} as const;

/**
 * Combined tariff availability (household + business)
 */
export const ALL_TARIFF_AVAILABILITY: Record<OSDCode, TariffCode[]> = {
  TAURON: [...TARIFF_AVAILABILITY.TAURON, ...BUSINESS_TARIFF_AVAILABILITY.TAURON],
  PGE: [...TARIFF_AVAILABILITY.PGE, ...BUSINESS_TARIFF_AVAILABILITY.PGE],
  ENEA: [...TARIFF_AVAILABILITY.ENEA, ...BUSINESS_TARIFF_AVAILABILITY.ENEA],
  ENERGA: [...TARIFF_AVAILABILITY.ENERGA, ...BUSINESS_TARIFF_AVAILABILITY.ENERGA],
};

// ============================================================================
// ZONE DEFINITIONS
// ============================================================================

/**
 * OSD regional zone
 */
export interface OSDZone {
  /** Zone code (e.g., "WARSZAWA", "KRAKOW") */
  code: string;
  /** Zone display name */
  name: string;
  /** Covered regions/voivodeships */
  regions?: string[];
}

// ============================================================================
// TARIFF PRICING STRUCTURES
// ============================================================================

/**
 * Fixed monthly fee by connection type
 */
export interface FixedMonthlyFee {
  /** Single-phase connection (1-fazowy) */
  singlePhase: number;
  /** Three-phase connection (3-fazowy) */
  threePhase: number;
}

/**
 * Time zone definition for tariffs
 */
export interface TimeZone {
  start: number; // Hour (0-23)
  end: number;   // Hour (0-23)
}

/**
 * Base tariff pricing (common fields)
 */
export interface BaseTariffPricing {
  /** Tariff display name */
  name: string;
  /** Tariff description */
  description?: string;
  /** Fixed monthly fee */
  fixedMonthlyFee: number | FixedMonthlyFee;
  /** Quality charge per kWh */
  qualityCharge: number;
}

/**
 * G11 - Single rate tariff pricing
 */
export interface G11Pricing extends BaseTariffPricing {
  /** Energy price per kWh (flat rate) */
  energyPricePerKwh: number;
  /** Distribution price per kWh (flat rate) */
  distributionPerKwh: number;
}

/**
 * G12 - Two-rate day/night tariff pricing
 */
export interface G12Pricing extends BaseTariffPricing {
  /** Energy price per kWh - day zone */
  energyDayPricePerKwh: number;
  /** Energy price per kWh - night zone */
  energyNightPricePerKwh: number;
  /** Distribution price per kWh - day zone */
  distributionDayPerKwh: number;
  /** Distribution price per kWh - night zone */
  distributionNightPerKwh: number;
  /** Day zone hours */
  dayHours?: TimeZone;
}

/**
 * G12as - Anti-smog tariff pricing (for electric heating)
 */
export interface G12asPricing extends BaseTariffPricing {
  /** Energy price per kWh */
  energyPricePerKwh: number;
  /** Distribution price per kWh - day zone */
  distributionDayPerKwh: number;
  /** Distribution price per kWh - night zone */
  distributionNightPerKwh: number;
}

/**
 * G12w - Weekend tariff pricing
 */
export interface G12wPricing extends BaseTariffPricing {
  /** Energy price per kWh */
  energyPricePerKwh: number;
  /** Distribution price per kWh - day/weekday zone */
  distributionDayPerKwh: number;
  /** Distribution price per kWh - night zone */
  distributionNightPerKwh: number;
  /** Distribution price per kWh - weekend zone */
  distributionWeekendPerKwh: number;
}

/**
 * G12n - PGE-specific night tariff pricing
 */
export interface G12nPricing extends BaseTariffPricing {
  /** Energy price per kWh - day zone */
  energyDayPricePerKwh: number;
  /** Energy price per kWh - night zone */
  energyNightPricePerKwh: number;
  /** Distribution price per kWh - day zone */
  distributionDayPerKwh: number;
  /** Distribution price per kWh - night zone */
  distributionNightPerKwh: number;
  /** Night hours (different from G12) */
  nightHours?: {
    weekday?: TimeZone;
    weekend?: 'all_day' | TimeZone;
  };
}

/**
 * G12r - Energa-specific extended weekend tariff pricing
 */
export interface G12rPricing extends BaseTariffPricing {
  /** Energy price per kWh */
  energyPricePerKwh: number;
  /** Distribution price per kWh - peak zone */
  distributionPeakPerKwh: number;
  /** Distribution price per kWh - off-peak zone */
  distributionOffPeakPerKwh: number;
}

/**
 * G13 - Three-rate seasonal tariff pricing
 */
export interface G13Pricing extends BaseTariffPricing {
  /** Energy price per kWh */
  energyPricePerKwh: number;
  /** Distribution price per kWh - peak zone */
  distributionPeakPerKwh: number;
  /** Distribution price per kWh - off-peak zone */
  distributionOffPeakPerKwh: number;
  /** Peak hours definition */
  peakHours?: {
    morning?: TimeZone;
    eveningWinter?: TimeZone;
    eveningSummer?: TimeZone;
  };
}

/**
 * G13s - Dynamic solar tariff pricing
 */
export interface G13sPricing extends BaseTariffPricing {
  /** Energy price per kWh */
  energyPricePerKwh: number;
  /** Distribution price per kWh - peak zone */
  distributionPeakPerKwh: number;
  /** Distribution price per kWh - off-peak zone */
  distributionOffPeakPerKwh: number;
  /** Solar hours definition */
  solarHours?: {
    summer?: TimeZone;
    winter?: TimeZone;
  };
}

/**
 * G14 - Dynamic hourly RCE tariff pricing
 */
export interface G14Pricing extends BaseTariffPricing {
  /** Distribution price per kWh - green zone */
  distributionGreenPerKwh: number;
  /** Distribution price per kWh - light green zone */
  distributionLightGreenPerKwh: number;
  /** Distribution price per kWh - yellow zone */
  distributionYellowPerKwh: number;
  /** Distribution price per kWh - red zone */
  distributionRedPerKwh: number;
  /** Average distribution price (for estimation) */
  avgDistributionPerKwh: number;
}

// ============================================================================
// BUSINESS TARIFF PRICING STRUCTURES (C-SERIES)
// ============================================================================

/**
 * Power fee structure for business tariffs
 */
export interface BusinessPowerFee {
  /** Fee per kW of contracted power per month */
  perKwPerMonth: number;
  /** Minimum contracted power (kW) */
  minPower?: number;
  /** Maximum contracted power (kW) */
  maxPower?: number;
}

/**
 * Base business tariff pricing
 */
export interface BaseBusinessTariffPricing extends BaseTariffPricing {
  /** Tariff category */
  category: 'BUSINESS';
  /** Power group (C1 ≤40kW, C2 >40kW) */
  powerGroup: 'C1' | 'C2';
  /** Maximum contracted power for this tariff (kW) */
  maxContractedPower?: number;
  /** Power fee structure */
  powerFee: BusinessPowerFee;
}

/**
 * C11 - Single rate business tariff (≤40 kW)
 */
export interface C11Pricing extends BaseBusinessTariffPricing {
  powerGroup: 'C1';
  /** Energy price per kWh (flat rate) */
  energyPricePerKwh: number;
  /** Distribution price per kWh (flat rate) */
  distributionPerKwh: number;
}

/**
 * C12a - Two-rate business tariff, variant A (≤40 kW)
 */
export interface C12aPricing extends BaseBusinessTariffPricing {
  powerGroup: 'C1';
  /** Energy price per kWh - peak zone */
  energyPeakPricePerKwh: number;
  /** Energy price per kWh - off-peak zone */
  energyOffPeakPricePerKwh: number;
  /** Distribution price per kWh - peak zone */
  distributionPeakPerKwh: number;
  /** Distribution price per kWh - off-peak zone */
  distributionOffPeakPerKwh: number;
}

/**
 * C12b - Two-rate business tariff, variant B (≤40 kW)
 */
export interface C12bPricing extends BaseBusinessTariffPricing {
  powerGroup: 'C1';
  /** Energy price per kWh - day zone */
  energyDayPricePerKwh: number;
  /** Energy price per kWh - night zone */
  energyNightPricePerKwh: number;
  /** Distribution price per kWh - day zone */
  distributionDayPerKwh: number;
  /** Distribution price per kWh - night zone */
  distributionNightPerKwh: number;
}

/**
 * C21 - Single rate business tariff (>40 kW)
 */
export interface C21Pricing extends BaseBusinessTariffPricing {
  powerGroup: 'C2';
  /** Energy price per kWh (flat rate) */
  energyPricePerKwh: number;
  /** Distribution price per kWh (flat rate) */
  distributionPerKwh: number;
}

/**
 * C22a - Two-rate business tariff, variant A (>40 kW)
 */
export interface C22aPricing extends BaseBusinessTariffPricing {
  powerGroup: 'C2';
  /** Energy price per kWh - peak zone */
  energyPeakPricePerKwh: number;
  /** Energy price per kWh - off-peak zone */
  energyOffPeakPricePerKwh: number;
  /** Distribution price per kWh - peak zone */
  distributionPeakPerKwh: number;
  /** Distribution price per kWh - off-peak zone */
  distributionOffPeakPerKwh: number;
}

/**
 * C22b - Two-rate business tariff, variant B (>40 kW)
 */
export interface C22bPricing extends BaseBusinessTariffPricing {
  powerGroup: 'C2';
  /** Energy price per kWh - day zone */
  energyDayPricePerKwh: number;
  /** Energy price per kWh - night zone */
  energyNightPricePerKwh: number;
  /** Distribution price per kWh - day zone */
  distributionDayPerKwh: number;
  /** Distribution price per kWh - night zone */
  distributionNightPerKwh: number;
}

/**
 * Union type for all household tariff pricing types
 */
export type HouseholdTariffPricing =
  | G11Pricing
  | G12Pricing
  | G12asPricing
  | G12wPricing
  | G12nPricing
  | G12rPricing
  | G13Pricing
  | G13sPricing
  | G14Pricing;

/**
 * Union type for all business tariff pricing types
 */
export type BusinessTariffPricing =
  | C11Pricing
  | C12aPricing
  | C12bPricing
  | C21Pricing
  | C22aPricing
  | C22bPricing;

/**
 * Union type for all tariff pricing types
 */
export type TariffPricing = HouseholdTariffPricing | BusinessTariffPricing;

// ============================================================================
// FEE STRUCTURES
// ============================================================================

/**
 * OSD fee structure
 */
export interface OSDFees {
  /** Handling fee (opłata handlowa) */
  handlingFee: {
    /** Regulated (URE) - usually 0 */
    regulated: number;
    /** Commercial basic */
    commercialBasic: number;
    /** Commercial premium (optional) */
    commercialPremium?: number;
    description?: string;
  };
  /** OZE fee (renewable energy fee) */
  ozeFee: {
    perKwh: number;
    perMwh?: number;
    description?: string;
  };
  /** Cogeneration fee */
  cogenerationFee: {
    perKwh: number;
    perMwh?: number;
    description?: string;
  };
  /** Subscription fee (opłata abonamentowa) */
  subscriptionFee: {
    monthly: number;
    description?: string;
  };
  /** Transition fee (opłata przejściowa) - ends 2026 */
  transitionFee: {
    tier1: number;
    tier2: number;
    tier3: number;
    description?: string;
  };
  /** Power fee (opłata mocowa) */
  powerFee: {
    tier1: number;
    tier2: number;
    tier3: number;
    tier4: number;
    tierThresholds: {
      tier1Max: number;
      tier2Max: number;
      tier3Max: number;
    };
    frozenPeriod?: {
      startMonth: number;
      endMonth: number;
      year: number;
    } | null;
    description?: string;
  };
}

// ============================================================================
// OSD PRICING CONFIG
// ============================================================================

/**
 * Complete OSD pricing configuration for a specific year
 */
export interface OSDPricingConfig {
  /** OSD code */
  osd: OSDCode;
  /** Pricing year */
  year: number;
  /** Description */
  description: string;
  /** Data source reference */
  source: string;
  /** VAT rate (e.g., 0.23 for 23%) */
  vatRate: number;
  /** Valid from date (ISO format) */
  validFrom?: string;
  /** Valid to date (ISO format) */
  validTo?: string;

  /** Regional zones (optional, for zone-specific pricing) */
  zones?: OSDZone[];

  /** Available tariffs for this OSD */
  availableTariffs: TariffCode[];

  /** Fee structure */
  fees: OSDFees;

  /** Household tariff pricing data (G-series) */
  tariffs: {
    g11: G11Pricing;
    g12: G12Pricing;
    g12w?: G12wPricing;
    g12as?: G12asPricing;
    g12n?: G12nPricing;
    g12r?: G12rPricing;
    g13?: G13Pricing;
    g13s?: G13sPricing;
    g14?: G14Pricing;
  };

  /** Business tariff pricing data (C-series) */
  businessTariffs?: {
    c11?: C11Pricing;
    c12a?: C12aPricing;
    c12b?: C12bPricing;
    c21?: C21Pricing;
    c22a?: C22aPricing;
    c22b?: C22bPricing;
  };

  /** Price freeze info (government intervention) */
  priceFreeze?: {
    active: boolean;
    maxGrossPrice: number;
    freezeEnd: string;
  };

  /** Year-over-year changes description */
  changes?: Record<string, string>;
}

// ============================================================================
// OSD METADATA (OPERATOR INFO)
// ============================================================================

/**
 * Special tariff metadata
 */
export interface SpecialTariffInfo {
  /** Description */
  description: string;
  /** Requires smart meter */
  requiresSmartMeter?: boolean;
  /** Eligibility requirements */
  requirements?: string[];
}

/**
 * OSD operator metadata
 */
export interface OSDOperator {
  /** Operator code */
  code: OSDCode;
  /** Full legal name */
  name: string;
  /** Short display name */
  shortName: string;
  /** Logo URL/path */
  logo: string;
  /** Official website */
  website: string;
  /** Service area (voivodeships) */
  coverage: string[];
  /** Regional zones */
  zones: OSDZone[];
  /** Available tariff codes */
  availableTariffs: TariffCode[];
  /** Special tariff information */
  specialTariffs?: Partial<Record<TariffCode, SpecialTariffInfo>>;
}

/**
 * OSD metadata container
 */
export interface OSDMetadata {
  /** All OSD operators */
  operators: OSDOperator[];
  /** Last update timestamp */
  lastUpdated?: string;
  /** Data version */
  version?: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if a tariff is available for an OSD
 */
export function isTariffAvailable(osd: OSDCode, tariff: TariffCode): boolean {
  if (isBusinessTariff(tariff)) {
    return BUSINESS_TARIFF_AVAILABILITY[osd]?.includes(tariff) ?? false;
  }
  return TARIFF_AVAILABILITY[osd]?.includes(tariff as HouseholdTariffCode) ?? false;
}

/**
 * Get available tariffs for an OSD
 */
export function getAvailableTariffs(osd: OSDCode): TariffCode[] {
  return [...(TARIFF_AVAILABILITY[osd] ?? [])];
}

/**
 * Check if an OSD code is valid
 */
export function isValidOSD(code: string): code is OSDCode {
  return OSD_CODES.includes(code as OSDCode);
}

/**
 * Get display name for OSD
 */
export function getOSDDisplayName(osd: OSDCode): string {
  const names: Record<OSDCode, string> = {
    TAURON: 'Tauron Dystrybucja',
    PGE: 'PGE Dystrybucja',
    ENEA: 'Enea Operator',
    ENERGA: 'Energa-Operator',
  };
  return names[osd];
}

// ============================================================================
// BUSINESS TARIFF HELPER FUNCTIONS
// ============================================================================

/**
 * All business tariff codes
 */
export const BUSINESS_TARIFF_CODES: readonly BusinessTariffCode[] = [
  'C11', 'C12a', 'C12b', 'C21', 'C22a', 'C22b'
] as const;

/**
 * C1 group tariffs (≤40 kW)
 */
export const C1_TARIFF_CODES: readonly BusinessTariffCode[] = ['C11', 'C12a', 'C12b'] as const;

/**
 * C2 group tariffs (>40 kW)
 */
export const C2_TARIFF_CODES: readonly BusinessTariffCode[] = ['C21', 'C22a', 'C22b'] as const;

/**
 * Check if a tariff code is a business tariff
 */
export function isBusinessTariff(tariff: TariffCode): tariff is BusinessTariffCode {
  return BUSINESS_TARIFF_CODES.includes(tariff as BusinessTariffCode);
}

/**
 * Check if a tariff code is a household tariff
 */
export function isHouseholdTariff(tariff: TariffCode): tariff is HouseholdTariffCode {
  return !isBusinessTariff(tariff);
}

/**
 * Get business tariffs available for an OSD
 */
export function getBusinessTariffsForOSD(osd: OSDCode): BusinessTariffCode[] {
  return [...(BUSINESS_TARIFF_AVAILABILITY[osd] ?? [])];
}

/**
 * Get household tariffs available for an OSD
 */
export function getHouseholdTariffsForOSD(osd: OSDCode): HouseholdTariffCode[] {
  return [...(TARIFF_AVAILABILITY[osd] ?? [])];
}

/**
 * Check if a business tariff is available for an OSD
 */
export function isBusinessTariffAvailable(osd: OSDCode, tariff: BusinessTariffCode): boolean {
  return BUSINESS_TARIFF_AVAILABILITY[osd]?.includes(tariff) ?? false;
}

/**
 * Get power group for a business tariff
 */
export function getBusinessTariffPowerGroup(tariff: BusinessTariffCode): 'C1' | 'C2' {
  return C1_TARIFF_CODES.includes(tariff) ? 'C1' : 'C2';
}

/**
 * Get business tariffs for a specific power group
 */
export function getTariffsForPowerGroup(powerGroup: 'C1' | 'C2'): BusinessTariffCode[] {
  return powerGroup === 'C1' ? [...C1_TARIFF_CODES] : [...C2_TARIFF_CODES];
}

/**
 * Check if contracted power is valid for tariff
 */
export function isValidContractedPower(tariff: BusinessTariffCode, powerKw: number): boolean {
  const group = getBusinessTariffPowerGroup(tariff);
  if (group === 'C1') {
    return powerKw > 0 && powerKw <= 40;
  }
  return powerKw > 40;
}

/**
 * Get all tariffs (household + business) for an OSD
 */
export function getAllTariffsForOSD(osd: OSDCode): TariffCode[] {
  return [...(ALL_TARIFF_AVAILABILITY[osd] ?? [])];
}

export default {
  OSD_CODES,
  TARIFF_AVAILABILITY,
  BUSINESS_TARIFF_AVAILABILITY,
  ALL_TARIFF_AVAILABILITY,
  BUSINESS_TARIFF_CODES,
  C1_TARIFF_CODES,
  C2_TARIFF_CODES,
  isTariffAvailable,
  getAvailableTariffs,
  isValidOSD,
  getOSDDisplayName,
  isBusinessTariff,
  isHouseholdTariff,
  getBusinessTariffsForOSD,
  getHouseholdTariffsForOSD,
  isBusinessTariffAvailable,
  getBusinessTariffPowerGroup,
  getTariffsForPowerGroup,
  isValidContractedPower,
  getAllTariffsForOSD,
};
